/**
 * The evidence "Ask Iris" sends from the DS Repricing Activity log (CU-869fkdhjv phase 3, plan § 2.4, UX C4):
 * one event row (not a setup change) and the count cards.
 *
 * An event carries a whitelist of what its row shows — type, time, office, ACRISS, product, trigger, strategy,
 * Δ price, resolution, the limits that capped it, whether it was simulated (so Iris never says "sent to the PMS"
 * of a dry run) — with office, product and rate card as the page's labels, never ids (criterion 28; ids only in
 * `params.ids`). Its derivation is the rail's: `derivationSteps` / `tetherSteps` (`./derivation.js`), the very
 * functions "How this was calculated" draws from (T17), flattened to one line per step.
 *
 * The page only exists with `ds_repricing` (`@constants/backConfigs.js:28`), so the engine travels here.
 * Pure and alias-free (TL R4).
 */
import { canvasState } from '../../../../@utils/askIris/canvasState.js'
import { CAP_LABELS, derivationSteps, tetherSteps } from './derivation.js'
import { sentStyleOf } from './sendStyle.js'
import { isNotSentState, notSentDetail } from './notSentReason.js'

//: `HH:MM` of the server's naive ISO stamps, as the detail reads them; no dates library here (pure).
const hhmm = (iso) => (typeof iso === 'string' && iso.length >= 16 ? iso.slice(11, 16) : '')

const VIEW = 'ds_repricing.activity'
const SCREEN = 'Activity log'

const QUESTION_BY_TYPE = { capped: 'event.capped', quarantined: 'event.held', pms_error: 'event.failed', applied: 'event.applied' }

/** The question of an event row; `setup_change` has none (not a decision of the engine, C4). */
export function eventQuestionKind(row) {
	const type = row?.event_type
	if (!type || type === 'setup_change' || row?.setup) return null
	return QUESTION_BY_TYPE[type] || 'event.generic'
}

const compact = obj => Object.fromEntries(Object.entries(obj).filter(([, v]) => v != null && v !== '' && !(Array.isArray(v) && !v.length)))

/** A derivation model (`derivationSteps` / `tetherSteps`) as one line per step, the rail's words. */
export function derivationLines(model) {
	if (!model) return []
	return model.steps.map(s => {
		if (s.type === 'step') return `${s.operator}${s.note ? ` (${s.note})` : ''}`
		if (s.type === 'result') {
			const delta = s.delta != null ? `${s.delta > 0 ? '+' : ''}${s.delta.toFixed(2)} €` : null
			return ['Result: ' + s.amount, s.was, delta, s.simulated ? 'dry run — nothing written' : s.unwritten].filter(Boolean).join(' · ')
		}
		return `${s.label}: ${s.amount}${s.notes.length ? ` — ${s.notes.join('; ')}` : ''}`
	})
}

const railOf = row => (row.via_tether ? tetherSteps(row) : derivationSteps(row))

/** What the rail's figures are: every node a total for the rental (`totalOf`), only the result also per day. */
export const DERIVATION_UNIT = 'totals for the rental; the result also per day'

/**
 * A decision's "How this was calculated", as Iris reads it: `{ derivation_of, unit, lines }` — the rail's caption,
 * its unit and one line per step, verbatim and in order. The one builder: Activity's event evidence and Last
 * changes' cell evidence (CU-869fn0gcp v1.4) both send this. `derivation_of` is null and `lines` empty when the row
 * has nothing to derive.
 */
export function derivationEvidence(row) {
	const rail = row ? railOf(row) : null
	return { derivation_of: rail?.caption || null, unit: DERIVATION_UNIT, lines: derivationLines(rail) }
}

/**
 * `labels`: what the page names the row by — `event` (its chip, `EVENT_META`), `office` (the office column),
 * `product` (the product, or the rate card of the model before), from the page's catalogues.
 */
export function eventEvidence({ row, labels = {} }) {
	const kind = eventQuestionKind(row)
	if (!kind) return null
	const scope = row.scope || {}
	const result = row.new_price ?? row.pushed_price
	const delta = row.delta ?? (result == null || row.previous_price == null ? null : Math.round((result - row.previous_price) * 100) / 100)
	const derivation = derivationEvidence(row)
	const office = labels.office || scope.office || null
	const derivationTitle = derivation.derivation_of ? { derivation_of: derivation.derivation_of } : {}
	// What became of the send, in the Resolution column's words, and — on a "not sent" — why (CU-869f03vek):
	// without it Iris had no fact that decides the case (the search sent once, the sibling site, the paused drain).
	const sendStatus = row.write_status || (row.simulated ? 'dry_run' : null)
	const send = sendStatus ? sentStyleOf(sendStatus).label : null
	const sendReason = isNotSentState(sendStatus) ? notSentDetail(row, hhmm) : null
	const winnerEventId = row.not_sent_reason?.winner_event_id || null
	return {
		kind,
		labels: [office, scope.sipp, scope.pickup_date ? { date: scope.pickup_date } : null, scope.duration_days != null ? { days: scope.duration_days } : null],
		state: canvasState({
			view: VIEW, screen: SCREEN,
			selection: compact({
				event: labels.event || row.event_type,
				time: row.ts || null,
				office,
				acriss: scope.sipp || null,
				pickup_date: scope.pickup_date || null,
				length_days: scope.duration_days ?? null,
				product: labels.product || labels.face || null,
				trigger: scope.source || null,
				strategy: row.strategy_name || null,
				previous_price: row.previous_price ?? null,
				new_price: result ?? null,
				delta,
				resolution: row.resolution || null,
				send,
				send_reason: sendReason,
				capped_by: (row.capped_by || []).map(k => CAP_LABELS[k] || k),
				simulated: !!row.simulated,
			}),
			digest: compact({ ...derivationTitle, derivation: derivation.lines, unit: derivation.unit }),
			params: { scope: compact({ office }), ids: compact({ event_id: row.id, execution_id: scope.execution_id, winner_event_id: winnerEventId }) },
			definition: 'event',
		}),
	}
}

/**
 * A count card with a value: `key` the counter, `label` the card's. Total asks about the period. `period` is a
 * question label (`{ lastDays }`, `{ range }`) or null on "All time". `pass` is the active Pass filter
 * (CU-869f03vek): the screen names a pass by its id and nothing else, so the id is the label here, and it
 * travels in `params.ids` too, where the row's does.
 */
export function countEvidence({ key, label, value, counters = {}, period = null, activeFilters = 0, pass = null }) {
	if (!value) return null
	const isTotal = key === 'total'
	return {
		kind: isTotal ? 'countTotal' : 'count',
		subject: isTotal ? undefined : label,
		labels: [isTotal ? { events: value } : { value: String(value) }, period],
		state: canvasState({
			view: VIEW, screen: SCREEN,
			selection: compact({ card: label, value, period: period?.lastDays != null ? `last ${period.lastDays} days` : (period?.range ? period.range.join(' – ') : 'all time'), active_filters: activeFilters, pass }),
			digest: { counters },
			params: pass ? { ids: { execution_id: pass } } : {},
			definition: 'count',
		}),
	}
}

/** The evidence of the screen goes when the filters or the period change — never on the live poll (T20). */
export function shouldClearAskContext(prev, next) {
	if (!prev) return false
	return JSON.stringify(prev) !== JSON.stringify(next)
}
